import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { QRCodeCanvas } from 'qrcode.react';
import { subscribeToBindingToken } from '../../lib/lineAuth';

const UserProfileModal = ({
  isOpen,
  onClose,
  user,
  currentUserProfile,
  onProfileUpdated
}) => {
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' | 'password' | 'line'

  // Profile & Avatar State
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState(null);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState(null);
  const fileInputRef = useRef(null);

  // Password Change State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  // LINE Binding State
  const [isRebinding, setIsRebinding] = useState(false);
  const [bindingToken, setBindingToken] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [checkingBinding, setCheckingBinding] = useState(false);
  const [lineMessage, setLineMessage] = useState(null);

  const profileId = currentUserProfile?.id || user?.id;

  // Sync recovery email when profile changes
  useEffect(() => {
    if (currentUserProfile?.recovery_email) {
      setRecoveryEmail(currentUserProfile.recovery_email);
    } else {
      setRecoveryEmail('');
    }
  }, [currentUserProfile?.recovery_email]);

  // Reset tab and messages on modal open
  useEffect(() => {
    if (isOpen) {
      setAvatarMessage(null);
      setEmailMessage(null);
      setPasswordMessage(null);
      setLineMessage(null);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsRebinding(false);
    }
  }, [isOpen]);

  // Image compression helper (max 500x500 JPEG base64)
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxDim = 500;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxDim) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            }
          } else {
            if (height > maxDim) {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.onerror = () => reject(new Error('ไม่สามารถประมวลผลไฟล์รูปภาพได้'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('เกิดข้อผิดพลาดในการอ่านไฟล์'));
      reader.readAsDataURL(file);
    });
  };

  // Handle local avatar file upload
  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setAvatarMessage({ type: 'error', text: 'กรุณาเลือกไฟล์รูปภาพเท่านั้น' });
      return;
    }

    setAvatarLoading(true);
    setAvatarMessage(null);

    try {
      const compressedDataUrl = await compressImage(file);
      if (!profileId || !supabase) {
        throw new Error('ไม่พบข้อมูลผู้ใช้');
      }

      const { error } = await supabase
        .from('user_profiles')
        .update({ avatar_url: compressedDataUrl })
        .eq('id', profileId);

      if (error) throw error;

      setAvatarMessage({ type: 'success', text: 'บันทึกรูปโปรไฟล์สำเร็จ' });
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Avatar upload error:', err);
      setAvatarMessage({ type: 'error', text: err.message || 'ไม่สามารถอัปเดตรูปโปรไฟล์ได้' });
    } finally {
      setAvatarLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Set avatar to LINE picture
  const handleUseLinePicture = async () => {
    if (!currentUserProfile?.line_picture_url || !profileId) return;

    setAvatarLoading(true);
    setAvatarMessage(null);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ avatar_url: currentUserProfile.line_picture_url })
        .eq('id', profileId);

      if (error) throw error;

      setAvatarMessage({ type: 'success', text: 'นำเข้ารูปโปรไฟล์จาก LINE สำเร็จ' });
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Use LINE pic error:', err);
      setAvatarMessage({ type: 'error', text: 'ไม่สามารถนำเข้ารูปจาก LINE ได้' });
    } finally {
      setAvatarLoading(false);
    }
  };

  // Remove avatar
  const handleRemoveAvatar = async () => {
    if (!profileId) return;
    setAvatarLoading(true);
    setAvatarMessage(null);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ avatar_url: null })
        .eq('id', profileId);

      if (error) throw error;

      setAvatarMessage({ type: 'success', text: 'ลบรูปโปรไฟล์เรียบร้อยแล้ว' });
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Remove avatar error:', err);
      setAvatarMessage({ type: 'error', text: 'ไม่สามารถลบรูปโปรไฟล์ได้' });
    } finally {
      setAvatarLoading(false);
    }
  };

  // Save Recovery Email
  const handleSaveRecoveryEmail = async (e) => {
    e.preventDefault();
    const cleanEmail = recoveryEmail.trim().toLowerCase();
    if (cleanEmail && (!cleanEmail.includes('@') || !cleanEmail.includes('.'))) {
      setEmailMessage({ type: 'error', text: 'กรุณากรอกรูปแบบอีเมลให้ถูกต้อง' });
      return;
    }

    setSavingEmail(true);
    setEmailMessage(null);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ recovery_email: cleanEmail || null })
        .eq('id', profileId);

      if (error) throw error;

      setEmailMessage({ type: 'success', text: 'บันทึกอีเมลสำหรับกู้คืนรหัสสำเร็จ' });
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Save recovery email error:', err);
      setEmailMessage({ type: 'error', text: 'ไม่สามารถบันทึกอีเมลได้' });
    } finally {
      setSavingEmail(false);
    }
  };

  // Handle Change Password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (!currentPassword) {
      setPasswordMessage({ type: 'error', text: 'กรุณาระบุรหัสผ่านปัจจุบัน' });
      return;
    }
    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'รหัสผ่านใหม่ต้องมีความยาวอย่างน้อย 6 ตัวอักษร' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'รหัสผ่านใหม่ทั้ง 2 ช่องไม่ตรงกัน' });
      return;
    }

    const authEmail = currentUserProfile?.email || user?.email;
    if (!authEmail) {
      setPasswordMessage({ type: 'error', text: 'ไม่พบอีเมลผู้ใช้งานสำหรับยืนยันตัวตน' });
      return;
    }

    setChangingPassword(true);

    try {
      // Step 1: Verify current password
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: authEmail,
        password: currentPassword
      });

      if (signInErr) {
        setPasswordMessage({ type: 'error', text: 'รหัสผ่านปัจจุบันไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง' });
        setChangingPassword(false);
        return;
      }

      // Step 2: Update password in Supabase Auth
      const { error: updateErr } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateErr) {
        throw updateErr;
      }

      setPasswordMessage({ type: 'success', text: '🎉 เปลี่ยนรหัสผ่านสำเร็จเรียบร้อยแล้ว' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Change password error:', err);
      setPasswordMessage({ type: 'error', text: err.message || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน' });
    } finally {
      setChangingPassword(false);
    }
  };

  // Create LINE Binding Token
  const initBindingToken = useCallback(async () => {
    if (!profileId || !supabase) return;
    setTokenLoading(true);
    setLineMessage(null);

    try {
      const { data: token, error } = await supabase.rpc('create_binding_token', {
        p_user_id: profileId
      });

      if (error) {
        console.error('create_binding_token error:', error);
        setLineMessage({ type: 'error', text: 'ไม่สามารถสร้างโทเค็นเชื่อมต่อ LINE ได้' });
        return;
      }

      setBindingToken(token);
    } catch (err) {
      console.error('Binding token exception:', err);
      setLineMessage({ type: 'error', text: 'เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์' });
    } finally {
      setTokenLoading(false);
    }
  }, [profileId]);

  // Subscribe and check status when in linking mode
  const checkBindingStatusNow = useCallback(async () => {
    if (!bindingToken || !supabase) return;
    setCheckingBinding(true);

    try {
      const { data, error } = await supabase.rpc('check_binding_status', { p_token: bindingToken });
      if (!error && Array.isArray(data) && data.length > 0) {
        const row = data[0];
        if (row.status === 'approved') {
          setLineMessage({ type: 'success', text: '✅ เชื่อมต่อบัญชี LINE สำเร็จเรียบร้อยแล้ว!' });
          setIsRebinding(false);
          if (onProfileUpdated) onProfileUpdated();
        } else if (row.status === 'scanned') {
          setLineMessage({ type: 'info', text: '📱 ตรวจพบการสแกนแล้ว กำลังรอยืนยันบนมือถือ...' });
        }
      }
    } catch (e) {
      console.warn('Check binding status error:', e);
    } finally {
      setCheckingBinding(false);
    }
  }, [bindingToken, onProfileUpdated]);

  // Trigger token creation when switching to linking mode
  useEffect(() => {
    const isLinked = Boolean(currentUserProfile?.line_user_id);
    if (isOpen && activeTab === 'line' && (!isLinked || isRebinding)) {
      initBindingToken();
    }
  }, [isOpen, activeTab, currentUserProfile?.line_user_id, isRebinding, initBindingToken]);

  // Listen to realtime token approval & window focus
  useEffect(() => {
    if (!bindingToken) return;

    const unsubscribe = subscribeToBindingToken(bindingToken, (approvedData) => {
      if (approvedData?.line_user_id || approvedData?.userId) {
        setLineMessage({ type: 'success', text: '✅ เชื่อมต่อบัญชี LINE สำเร็จเรียบร้อยแล้ว!' });
        setIsRebinding(false);
        if (onProfileUpdated) onProfileUpdated();
      }
    });

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkBindingStatusNow();
      }
    };
    const onFocus = () => {
      checkBindingStatusNow();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      if (unsubscribe) unsubscribe();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [bindingToken, checkBindingStatusNow, onProfileUpdated]);

  // Unlink LINE account
  const handleUnlinkLine = async () => {
    if (!window.confirm('คุณต้องการยกเลิกการเชื่อมต่อบัญชี LINE นี้ใช่หรือไม่?')) {
      return;
    }

    setTokenLoading(true);
    setLineMessage(null);

    try {
      const { error } = await supabase.rpc('unlink_line_account', {
        p_user_id: profileId
      });

      if (error) throw error;

      setLineMessage({ type: 'success', text: 'ยกเลิกการเชื่อมต่อ LINE สำเร็จ' });
      setIsRebinding(false);
      if (onProfileUpdated) onProfileUpdated();
    } catch (err) {
      console.error('Unlink line error:', err);
      setLineMessage({ type: 'error', text: 'ไม่สามารถยกเลิกการเชื่อมต่อ LINE ได้' });
    } finally {
      setTokenLoading(false);
    }
  };

  if (!isOpen) return null;

  const isLineLinked = Boolean(currentUserProfile?.line_user_id);
  const currentAvatar = currentUserProfile?.avatar_url || currentUserProfile?.line_picture_url;
  const bindingUrl =
    typeof window !== 'undefined' && bindingToken
      ? `${window.location.origin}/?action=bind-line&token=${bindingToken}&userId=${profileId || ''}`
      : '';

  return (
    <div
      data-testid="user-profile-modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px',
        boxSizing: 'border-box'
      }}
    >
      <div
        data-testid="user-profile-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '560px',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px 16px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#ffffff'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}
            >
              ⚙️
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.25em', fontWeight: '800', color: '#0f172a' }}>
                จัดการข้อมูลส่วนตัว
              </h3>
              <p style={{ margin: 0, fontSize: '0.82em', color: '#64748b' }}>
                {currentUserProfile?.name || user?.email || 'บัญชีผู้ใช้งาน'}
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="close-profile-modal-btn"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '8px',
              lineHeight: 1
            }}
            title="ปิดหน้าต่าง"
          >
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1.5px solid #f1f5f9',
            backgroundColor: '#f8fafc',
            padding: '0 16px'
          }}
        >
          <button
            type="button"
            data-testid="tab-profile-btn"
            onClick={() => setActiveTab('profile')}
            style={{
              flex: 1,
              padding: '14px 12px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'profile' ? '2.5px solid #2563eb' : '2.5px solid transparent',
              color: activeTab === 'profile' ? '#2563eb' : '#64748b',
              fontWeight: activeTab === 'profile' ? '700' : '600',
              fontSize: '0.9em',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <span>👤</span>
            <span>โปรไฟล์</span>
          </button>

          <button
            type="button"
            data-testid="tab-password-btn"
            onClick={() => setActiveTab('password')}
            style={{
              flex: 1,
              padding: '14px 12px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'password' ? '2.5px solid #2563eb' : '2.5px solid transparent',
              color: activeTab === 'password' ? '#2563eb' : '#64748b',
              fontWeight: activeTab === 'password' ? '700' : '600',
              fontSize: '0.9em',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <span>🔑</span>
            <span>เปลี่ยนรหัสผ่าน</span>
          </button>

          <button
            type="button"
            data-testid="tab-line-btn"
            onClick={() => setActiveTab('line')}
            style={{
              flex: 1,
              padding: '14px 12px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'line' ? '2.5px solid #06C755' : '2.5px solid transparent',
              color: activeTab === 'line' ? '#059669' : '#64748b',
              fontWeight: activeTab === 'line' ? '700' : '600',
              fontSize: '0.9em',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <span>💬</span>
            <span>เชื่อมต่อ LINE</span>
            {isLineLinked && (
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#06C755',
                  display: 'inline-block'
                }}
              />
            )}
          </button>
        </div>

        {/* Modal Body / Tab Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* TAB 1: Profile & Avatar */}
          {activeTab === 'profile' && (
            <div>
              {/* Avatar Section */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginBottom: '24px'
                }}
              >
                <div style={{ position: 'relative', marginBottom: '12px' }}>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      backgroundColor: '#f1f5f9',
                      border: '3px solid #e2e8f0',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      position: 'relative'
                    }}
                    title="คลิกเพื่ออัปโหลดรูปภาพใหม่"
                  >
                    {currentAvatar ? (
                      <img
                        src={currentAvatar}
                        alt="Avatar"
                        data-testid="profile-avatar-img"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{ fontSize: '42px', color: '#94a3b8' }}>👤</div>
                    )}

                    {avatarLoading && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff',
                          fontSize: '20px'
                        }}
                      >
                        ⏳
                      </div>
                    )}
                  </div>

                  {/* Camera Icon Overlay */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      border: '2px solid #ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '15px',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                    }}
                    title="เปลี่ยนรูปภาพโปรไฟล์"
                  >
                    📷
                  </button>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarFileChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                  data-testid="avatar-file-input"
                />

                {/* Quick Avatar Actions */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={avatarLoading}
                    style={{
                      padding: '6px 12px',
                      fontSize: '0.82em',
                      fontWeight: '600',
                      backgroundColor: '#f1f5f9',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: '#334155'
                    }}
                  >
                    📁 อัปโหลดรูปใหม่
                  </button>

                  {currentUserProfile?.line_picture_url && (
                    <button
                      type="button"
                      data-testid="use-line-pic-btn"
                      onClick={handleUseLinePicture}
                      disabled={avatarLoading}
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.82em',
                        fontWeight: '600',
                        backgroundColor: '#dcfce7',
                        border: '1px solid #86efac',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: '#15803d'
                      }}
                    >
                      💬 ใช้รูปจาก LINE
                    </button>
                  )}

                  {currentUserProfile?.avatar_url && (
                    <button
                      type="button"
                      data-testid="remove-avatar-btn"
                      onClick={handleRemoveAvatar}
                      disabled={avatarLoading}
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.82em',
                        fontWeight: '600',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: '#b91c1c'
                      }}
                    >
                      🗑️ ลบรูป
                    </button>
                  )}
                </div>

                {avatarMessage && (
                  <div
                    style={{
                      marginTop: '8px',
                      fontSize: '0.82em',
                      color: avatarMessage.type === 'success' ? '#15803d' : '#b91c1c'
                    }}
                  >
                    {avatarMessage.text}
                  </div>
                )}
              </div>

              {/* Profile Fields Info */}
              <div
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '16px',
                  marginBottom: '20px'
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <span style={{ fontSize: '0.78em', color: '#64748b', display: 'block' }}>ชื่อ-นามสกุล</span>
                    <strong style={{ fontSize: '0.92em', color: '#0f172a' }}>
                      {currentUserProfile?.name ||
                        `${currentUserProfile?.first_name || ''} ${currentUserProfile?.last_name || ''}`.trim() ||
                        '-'}
                    </strong>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.78em', color: '#64748b', display: 'block' }}>บทบาท</span>
                    <span
                      style={{
                        fontSize: '0.78em',
                        fontWeight: '700',
                        color: '#2563eb',
                        backgroundColor: '#eff6ff',
                        padding: '2px 8px',
                        borderRadius: '9999px',
                        border: '1px solid #bfdbfe',
                        display: 'inline-block'
                      }}
                    >
                      {currentUserProfile?.role || 'เจ้าหน้าที่'}
                    </span>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.78em', color: '#64748b', display: 'block' }}>Username</span>
                    <strong style={{ fontSize: '0.92em', color: '#0f172a', fontFamily: 'monospace' }}>
                      {currentUserProfile?.username || '-'}
                    </strong>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.78em', color: '#64748b', display: 'block' }}>อีเมลล็อกอิน</span>
                    <span style={{ fontSize: '0.85em', color: '#334155' }}>
                      {currentUserProfile?.email || user?.email || '-'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Recovery Email Section */}
              <form onSubmit={handleSaveRecoveryEmail}>
                <label
                  htmlFor="profile-recovery-email"
                  style={{
                    display: 'block',
                    fontSize: '0.88em',
                    fontWeight: '700',
                    color: '#334155',
                    marginBottom: '6px'
                  }}
                >
                  อีเมลสำหรับกู้คืนรหัสผ่าน
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    id="profile-recovery-email"
                    type="email"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    placeholder="เช่น yourname@gmail.com"
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92em',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={savingEmail}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '0.88em',
                      fontWeight: '700',
                      cursor: savingEmail ? 'wait' : 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {savingEmail ? 'กำลังบันทึก...' : 'บันทึก'}
                  </button>
                </div>
                <div style={{ fontSize: '0.78em', color: '#64748b', marginTop: '4px' }}>
                  ใช้อีเมลจริงของท่านเพื่อรับลิงก์รีเซ็ตรหัสผ่านเมื่อลืมรหัส
                </div>

                {emailMessage && (
                  <div
                    style={{
                      marginTop: '8px',
                      fontSize: '0.82em',
                      color: emailMessage.type === 'success' ? '#15803d' : '#b91c1c'
                    }}
                  >
                    {emailMessage.text}
                  </div>
                )}
              </form>
            </div>
          )}

          {/* TAB 2: Change Password */}
          {activeTab === 'password' && (
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label
                    htmlFor="current-password"
                    style={{ fontSize: '0.88em', fontWeight: '700', color: '#334155' }}
                  >
                    รหัสผ่านปัจจุบัน <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '0.78em', cursor: 'pointer' }}
                  >
                    {showCurrentPassword ? '🙈 ซ่อน' : '👁️ แสดง'}
                  </button>
                </div>
                <input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="กรอกรหัสผ่านปัจจุบัน"
                  autoComplete="current-password"
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.92em',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label
                    htmlFor="new-password"
                    style={{ fontSize: '0.88em', fontWeight: '700', color: '#334155' }}
                  >
                    รหัสผ่านใหม่ <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '0.78em', cursor: 'pointer' }}
                  >
                    {showNewPassword ? '🙈 ซ่อน' : '👁️ แสดง'}
                  </button>
                </div>
                <input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="อย่างน้อย 6 ตัวอักษร"
                  autoComplete="new-password"
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: '10px',
                    border: newPassword.length >= 6 ? '1.5px solid #10b981' : '1.5px solid #cbd5e1',
                    fontSize: '0.92em',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label
                  htmlFor="confirm-new-password"
                  style={{ display: 'block', fontSize: '0.88em', fontWeight: '700', color: '#334155', marginBottom: '6px' }}
                >
                  ยืนยันรหัสผ่านใหม่ <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  id="confirm-new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="พิมพ์รหัสผ่านใหม่อีกครั้ง"
                  autoComplete="new-password"
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: '10px',
                    border:
                      confirmPassword && confirmPassword === newPassword
                        ? '1.5px solid #10b981'
                        : confirmPassword
                        ? '1.5px solid #f87171'
                        : '1.5px solid #cbd5e1',
                    fontSize: '0.92em',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                {confirmPassword && (
                  <div
                    style={{
                      fontSize: '0.78em',
                      marginTop: '4px',
                      color: confirmPassword === newPassword ? '#059669' : '#dc2626'
                    }}
                  >
                    {confirmPassword === newPassword ? '✓ รหัสผ่านตรงกัน' : '✗ รหัสผ่านไม่ตรงกัน'}
                  </div>
                )}
              </div>

              {passwordMessage && (
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    fontSize: '0.88em',
                    backgroundColor: passwordMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${passwordMessage.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
                    color: passwordMessage.type === 'success' ? '#15803d' : '#b91c1c'
                  }}
                >
                  {passwordMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={changingPassword || !currentPassword || newPassword.length < 6 || newPassword !== confirmPassword}
                style={{
                  width: '100%',
                  padding: '12px 18px',
                  backgroundColor:
                    !changingPassword && currentPassword && newPassword.length >= 6 && newPassword === confirmPassword
                      ? '#2563eb'
                      : '#94a3b8',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '0.95em',
                  fontWeight: '700',
                  cursor:
                    !changingPassword && currentPassword && newPassword.length >= 6 && newPassword === confirmPassword
                      ? 'pointer'
                      : 'not-allowed',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  transition: 'all 0.2s'
                }}
              >
                {changingPassword ? 'กำลังเปลี่ยนรหัสผ่าน...' : 'บันทึกรหัสผ่านใหม่'}
              </button>
            </form>
          )}

          {/* TAB 3: LINE Account Binding */}
          {activeTab === 'line' && (
            <div>
              {/* Case A: Already Linked and not in rebinding mode */}
              {isLineLinked && !isRebinding ? (
                <div>
                  <div
                    style={{
                      backgroundColor: '#f0fdf4',
                      border: '2px solid #86efac',
                      borderRadius: '16px',
                      padding: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)',
                      marginBottom: '20px'
                    }}
                  >
                    {currentUserProfile.line_picture_url ? (
                      <img
                        src={currentUserProfile.line_picture_url}
                        alt="LINE Avatar"
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2.5px solid #06C755'
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          backgroundColor: '#06C755',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '30px'
                        }}
                      >
                        💬
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          fontSize: '0.75em',
                          fontWeight: '700',
                          color: '#15803d',
                          backgroundColor: '#dcfce7',
                          padding: '2px 8px',
                          borderRadius: '9999px',
                          border: '1px solid #bbf7d0',
                          display: 'inline-block',
                          marginBottom: '4px'
                        }}
                      >
                        ✅ บัญชี LINE เชื่อมต่อแล้ว
                      </span>
                      <div
                        style={{
                          fontSize: '1.1em',
                          fontWeight: '800',
                          color: '#0f172a',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {currentUserProfile.line_display_name || 'LINE User'}
                      </div>
                      <div
                        style={{
                          fontSize: '0.78em',
                          color: '#64748b',
                          fontFamily: 'monospace',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        UID: {currentUserProfile.line_user_id}
                      </div>
                    </div>
                  </div>

                  <p style={{ fontSize: '0.85em', color: '#64748b', lineHeight: '1.5', margin: '0 0 16px 0' }}>
                    ท่านสามารถใช้บัญชี LINE นี้ในการเข้าสู่ระบบได้อย่างสะดวกผ่านการสแกน QR Code หรือปุ่มเข้าสู่ระบบด้วย LINE
                  </p>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      data-testid="rebind-line-btn"
                      onClick={() => setIsRebinding(true)}
                      style={{
                        flex: 1,
                        padding: '11px 16px',
                        backgroundColor: '#f1f5f9',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '12px',
                        fontSize: '0.9em',
                        fontWeight: '700',
                        color: '#334155',
                        cursor: 'pointer'
                      }}
                    >
                      🔄 เปลี่ยนบัญชี LINE
                    </button>

                    <button
                      type="button"
                      data-testid="unlink-line-btn"
                      onClick={handleUnlinkLine}
                      disabled={tokenLoading}
                      style={{
                        padding: '11px 16px',
                        backgroundColor: '#fee2e2',
                        border: '1.5px solid #fca5a5',
                        borderRadius: '12px',
                        fontSize: '0.9em',
                        fontWeight: '700',
                        color: '#b91c1c',
                        cursor: 'pointer'
                      }}
                    >
                      ❌ ยกเลิกการเชื่อมต่อ
                    </button>
                  </div>
                </div>
              ) : (
                /* Case B: Not Linked OR In Rebinding Mode */
                <div style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      backgroundColor: '#f8fafc',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '20px',
                      padding: '24px 16px',
                      marginBottom: '16px'
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-block',
                        padding: '12px',
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08)',
                        marginBottom: '12px'
                      }}
                    >
                      {tokenLoading || !bindingToken ? (
                        <div
                          style={{
                            width: 170,
                            height: 170,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            color: '#64748b',
                            fontSize: '0.85em'
                          }}
                        >
                          <div style={{ fontSize: '24px' }}>⏳</div>
                          กำลังสร้าง QR Code...
                        </div>
                      ) : (
                        <QRCodeCanvas
                          value={bindingUrl}
                          size={170}
                          level="M"
                          includeMargin={false}
                        />
                      )}
                    </div>

                    <div style={{ fontSize: '0.95em', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                      เปิดกล้องมือถือหรือแอป LINE เพื่อสแกน QR Code นี้
                    </div>
                    <div style={{ fontSize: '0.82em', color: '#64748b', marginBottom: '14px' }}>
                      (หากใช้งานบนมือถือ สามารถแตะที่ปุ่มสีเขียวด้านล่างได้ทันที)
                    </div>

                    {/* Primary Mobile Button */}
                    {bindingUrl && (
                      <a
                        href={bindingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="open-line-binding-btn"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          width: '100%',
                          padding: '14px 18px',
                          backgroundColor: '#06C755',
                          color: '#ffffff',
                          borderRadius: '14px',
                          fontWeight: '700',
                          fontSize: '0.95em',
                          textDecoration: 'none',
                          boxShadow: '0 4px 14px rgba(6, 199, 85, 0.35)',
                          boxSizing: 'border-box',
                          marginBottom: '12px'
                        }}
                      >
                        <span>📲</span>
                        <span>แตะเพื่อเปิด LINE ยืนยันตัวตน (สำหรับมือถือ)</span>
                      </a>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                      <div style={{ fontSize: '0.82em', color: '#059669', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '10px' }}>🟢</span>
                        <span>ระบบกำลังรอการยืนยันแบบ Real-time (ข้อมูลไม่หาย)</span>
                      </div>
                      <button
                        type="button"
                        data-testid="manual-check-binding-btn"
                        onClick={checkBindingStatusNow}
                        disabled={checkingBinding}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#2563eb',
                          cursor: 'pointer',
                          fontSize: '0.82em',
                          textDecoration: 'underline',
                          marginTop: '4px'
                        }}
                      >
                        {checkingBinding ? 'กำลังตรวจสอบ...' : '🔄 กดตรวจสอบสถานะการเชื่อมต่อ'}
                      </button>
                    </div>
                  </div>

                  {isRebinding && (
                    <button
                      type="button"
                      onClick={() => setIsRebinding(false)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: '10px',
                        color: '#64748b',
                        fontSize: '0.85em',
                        cursor: 'pointer'
                      }}
                    >
                      ยกเลิกการเปลี่ยนบัญชี
                    </button>
                  )}
                </div>
              )}

              {lineMessage && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    fontSize: '0.88em',
                    backgroundColor:
                      lineMessage.type === 'success'
                        ? '#f0fdf4'
                        : lineMessage.type === 'info'
                        ? '#eff6ff'
                        : '#fef2f2',
                    border: `1px solid ${
                      lineMessage.type === 'success'
                        ? '#bbf7d0'
                        : lineMessage.type === 'info'
                        ? '#bfdbfe'
                        : '#fecaca'
                    }`,
                    color:
                      lineMessage.type === 'success'
                        ? '#15803d'
                        : lineMessage.type === 'info'
                        ? '#1d4ed8'
                        : '#b91c1c',
                    textAlign: 'center'
                  }}
                >
                  {lineMessage.text}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
